import type { ActionContract } from './actions';
import type { WorkflowEdge, WorkflowGraph, WorkflowNode } from './xyflowMapping';
import { INTERNAL_ACTION_IDS, SPECIAL_ACTIONS } from './specialActions';

export type ValidationSeverity = 'error' | 'warning';

export interface ValidationIssue {
  severity: ValidationSeverity;
  message: string;
  nodeId?: string;
  edgeId?: string;
}

export interface ValidationResult {
  issues: ValidationIssue[];
  badNodeIds: Set<string>;
  badEdgeIds: Set<string>;
}

const buildAdjacency = (edges: WorkflowEdge[]): Map<string, Set<string>> => {
  const map = new Map<string, Set<string>>();
  edges.forEach((edge) => {
    if (edge.data?.kind !== 'control') return;
    let succ = map.get(edge.source);
    if (!succ) {
      succ = new Set();
      map.set(edge.source, succ);
    }
    succ.add(edge.target);
  });
  return map;
};

const detectCycles = (nodes: WorkflowNode[], adjacency: Map<string, Set<string>>): Set<string> => {
  const indegree = new Map<string, number>();
  nodes.forEach((n) => indegree.set(n.id, 0));
  adjacency.forEach((succ) => succ.forEach((id) => indegree.set(id, (indegree.get(id) ?? 0) + 1)));

  const queue: string[] = [];
  indegree.forEach((deg, id) => {
    if (deg === 0) queue.push(id);
  });
  const processed = new Set<string>();
  while (queue.length > 0) {
    const cur = queue.shift();
    if (cur === undefined) break;
    processed.add(cur);
    adjacency.get(cur)?.forEach((next) => {
      const d = (indegree.get(next) ?? 0) - 1;
      indegree.set(next, d);
      if (d === 0) queue.push(next);
    });
  }
  const cyclic = new Set<string>();
  indegree.forEach((deg, id) => {
    if (deg > 0 && !processed.has(id)) cyclic.add(id);
  });
  return cyclic;
};

const findInitial = (nodes: WorkflowNode[]): WorkflowNode | undefined =>
  nodes.find((n) => n.data.actionId === SPECIAL_ACTIONS.initial);

const reachableFrom = (start: string, adjacency: Map<string, Set<string>>): Set<string> => {
  const visited = new Set<string>([start]);
  const queue = [start];
  while (queue.length > 0) {
    const cur = queue.shift();
    if (cur === undefined) break;
    adjacency.get(cur)?.forEach((next) => {
      if (!visited.has(next)) {
        visited.add(next);
        queue.push(next);
      }
    });
  }

  return visited;
};

export const validateWorkflow = (
  graph: WorkflowGraph,
  contracts: ActionContract[],
): ValidationResult => {
  const issues: ValidationIssue[] = [];
  const badNodeIds = new Set<string>();
  const badEdgeIds = new Set<string>();

  const { nodes } = graph;
  const { edges } = graph;
  const adjacency = buildAdjacency(edges);

  // Self-loops in runAfter.
  edges.forEach((e) => {
    if (e.data?.kind === 'control' && e.source === e.target) {
      issues.push({
        severity: 'error',
        message: `Step "${e.source}" runs after itself.`,
        edgeId: e.id,
        nodeId: e.source,
      });
      badEdgeIds.add(e.id);
      badNodeIds.add(e.source);
    }
  });

  // Cycles (any node still having indegree > 0 after Kahn's algorithm).
  const cyclic = detectCycles(nodes, adjacency);
  cyclic.forEach((id) => {
    badNodeIds.add(id);
    issues.push({
      severity: 'error',
      message: `Step "${id}" is part of a circular dependency.`,
      nodeId: id,
    });
  });

  // Edges with both endpoints in the cycle are also visually marked.
  edges.forEach((e) => {
    if (e.data?.kind === 'control' && cyclic.has(e.source) && cyclic.has(e.target)) {
      badEdgeIds.add(e.id);
    }
  });

  // Reachability from Initial — flag unreachable non-terminal steps.
  const initial = findInitial(nodes);
  if (initial) {
    const reachable = reachableFrom(initial.id, adjacency);
    nodes.forEach((n) => {
      const aid = n.data.actionId;
      if (
        aid === SPECIAL_ACTIONS.initial ||
        aid === SPECIAL_ACTIONS.endIteration ||
        aid === SPECIAL_ACTIONS.output
      )
        return;
      // Steps nested inside a forEach are unreachable from Initial via control edges,
      // but they are still valid; skip them here.
      if (n.data.parentStepId) return;
      if (!reachable.has(n.id)) {
        badNodeIds.add(n.id);
        issues.push({
          severity: 'error',
          message: `Step "${n.id}" is not connected to Initial.`,
          nodeId: n.id,
        });
      }
    });
  }

  // Required-input validation: contract-declared inputs marked required must have a binding.
  const contractByActionId = new Map<string, ActionContract>();
  contracts.forEach((c) => contractByActionId.set(c.actionId, c));

  // StepOutput bindings must reference a step that is reachable via runAfter (or itself).
  const reverseAdjacency = new Map<string, Set<string>>();
  edges.forEach((edge) => {
    if (edge.data?.kind !== 'control') return;
    let preds = reverseAdjacency.get(edge.target);
    if (!preds) {
      preds = new Set();
      reverseAdjacency.set(edge.target, preds);
    }
    preds.add(edge.source);
  });

  const ancestors = (id: string): Set<string> => {
    const seen = new Set<string>();
    const queue = [id];
    while (queue.length > 0) {
      const cur = queue.shift();
      if (cur === undefined) break;
      reverseAdjacency.get(cur)?.forEach((src) => {
        if (!seen.has(src)) {
          seen.add(src);
          queue.push(src);
        }
      });
    }

    return seen;
  };

  const nodeById = new Map<string, WorkflowNode>();
  nodes.forEach((n) => nodeById.set(n.id, n));

  nodes.forEach((node) => {
    const contract = contractByActionId.get(node.data.actionId);
    if (contract) {
      contract.inputs.forEach((field) => {
        const binding = node.data.input?.[field.name];
        // Treat empty bindings as missing.
        const isEmpty =
          !binding ||
          (binding.kind === 'Constant' &&
            (binding.value === '' || binding.value === null || binding.value === undefined)) ||
          (binding.kind === 'ProcessingJobInput' && !binding.target) ||
          (binding.kind === 'StepOutput' && (!binding.stepId || !binding.target)) ||
          (binding.kind === 'Expression' && !binding.expression);
        if (field.required && isEmpty) {
          badNodeIds.add(node.id);
          issues.push({
            severity: 'error',
            message: `Step "${node.id}" is missing required input "${field.name}".`,
            nodeId: node.id,
          });
        }
      });
    }

    Object.entries(node.data.input ?? {}).forEach(([fieldName, binding]) => {
      if (binding.kind !== 'StepOutput') return;
      if (!binding.stepId) return;
      const sourceNode = nodeById.get(binding.stepId);
      if (!sourceNode) {
        badNodeIds.add(node.id);
        issues.push({
          severity: 'error',
          message: `Step "${node.id}" input "${fieldName}" references unknown step "${binding.stepId}".`,
          nodeId: node.id,
        });
        return;
      }
      if (binding.stepId === node.id) return;
      const ancestorSet = ancestors(node.id);
      if (ancestorSet.has(binding.stepId)) return;
      // A step downstream of a forEach can validly read outputs from any step nested
      // inside that forEach — the forEach aggregates its children's outputs on completion.
      const sourceParent = sourceNode.data.parentStepId;
      if (sourceParent && ancestorSet.has(sourceParent)) return;
      badNodeIds.add(node.id);
      issues.push({
        severity: 'warning',
        message: `Step "${node.id}" input "${fieldName}" reads from "${binding.stepId}", which is not in its runAfter chain.`,
        nodeId: node.id,
      });
    });
  });

  // Conditional run-after must target a CheckCondition step.
  edges.forEach((edge) => {
    if (edge.data?.kind !== 'control') return;
    if (edge.data.enableOnCondition !== true) return;
    const source = nodes.find((n) => n.id === edge.source);
    if (!source) return;
    if (source.data.actionId !== 'checkCondition') {
      badEdgeIds.add(edge.id);
      issues.push({
        severity: 'error',
        message: `Conditional connection from "${edge.source}" is only valid when the source is a CheckCondition step.`,
        edgeId: edge.id,
      });
    }
  });

  // Non-Initial / non-StartIteration steps must have at least one inbound runAfter.
  nodes.forEach((node) => {
    const aid = node.data.actionId;
    if (aid === SPECIAL_ACTIONS.initial || aid === SPECIAL_ACTIONS.startIteration) return;
    if (INTERNAL_ACTION_IDS.has(aid) && aid !== SPECIAL_ACTIONS.output) return;
    const hasIncoming = edges.some((e) => e.data?.kind === 'control' && e.target === node.id);
    if (!hasIncoming) {
      badNodeIds.add(node.id);
      issues.push({
        severity: 'error',
        message: `Step "${node.id}" has no incoming connections (orphan block).`,
        nodeId: node.id,
      });
    }
  });

  return { issues, badNodeIds, badEdgeIds };
};
